<script setup lang="ts">
import { useStorage } from "@vueuse/core";
import { ElMessageBox } from "element-plus";
import { computed, inject, onBeforeMount, ref } from "vue";
import {
  workflowInjectKey,
  WorkflowState,
} from "~/Compliance/composables/useWorkflow";
import DroppablePad from "~/NewFormBuilder/components/Designer/DroppablePad.vue";
import {
  type DragDropState,
  dragDropInjectKey,
} from "~/NewFormBuilder/composables/useDragDrop";
import {
  type FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";
import { FormDisplayMode } from "~/NewFormBuilder/types/FormDisplayMode";
import {
  type RepeaterFormElement,
  type Workflow,
  FormElementBase,
  FormElementRuleType,
  FormElementType,
  RepeaterHostAnswerValue,
  RuleConditionBase,
  RuleConditionType,
  StatusBasedConditionType,
  WorkflowStatusRuleCondition,
} from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";
import type { DefaultFormComponentProps } from "./FormComponentRenderer.vue";
import FormComponentRenderer from "./FormComponentRenderer.vue";

const showArchived = useStorage("show-archived", false);

const myInfoStore = useMyInfoStore();
const props = defineProps<DefaultFormComponentProps<RepeaterFormElement>>();

const { form, formResponse, useAnswer, reloadAnswers } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
const { getWorkflowStatus } = inject(workflowInjectKey, {
  getWorkflowStatus: (): string => {
    return "";
  },
  updateWorkflowStatus: async (): Promise<void> => {},
  updateWorkflowStatusFromNameStamp: async (): Promise<void> => {},
  getWorkflow: (): Workflow | undefined => undefined,
}) as WorkflowState;
const {
  isDragging = ref(false),
  handleDropComponent = async () => {},
  handleClickComponent = () => {},
  handleOnDrag = () => {},
  editingComponent = computed(() => undefined),
  canDropInRepeater = ref(false),
} = (inject(dragDropInjectKey, null) as DragDropState | null) || {};

const answer = useAnswer<FormElementType.REPEATER>(props.modelValue);

const elementProperties = computed(() => props.modelValue.properties);

const testingAnswer = ref<RepeaterHostAnswerValue>({
  repeatableGroupGuids: [emptyGuid],
});

const repeatersComponents = computed(() =>
  form.components.filter((c) => c.parentGuid === props.modelValue.guid),
);

/**
 * this method calls the api to create a repeated section
 * @param numberOfRepeats
 */
const createRepeatedSection = async (numberOfRepeats: number = 1) => {
  if (!formResponse) {
    const randomFourDigitNumber = Math.floor(1000 + Math.random() * 9000);
    const newGuid = "00000000-0000-0000-0000-00000000" + randomFourDigitNumber;
    testingAnswer.value.repeatableGroupGuids.push(newGuid);
    return;
  }

  await apiClient.formResponseAnswer
    .createRepeaterAnswer(
      formResponse.formDefinitionGuid,
      formResponse.guid,
      props.modelValue.guid,
      myInfoStore.currentSysOrgGuid,
      numberOfRepeats,
    )
    .then((res) => {
      reloadAnswers(res);
    })
    .catch(showError);
};

const currentWorkflowStatus = computed(() => {
  // will this update if the workflow changes?
  return getWorkflowStatus();
});

const isReadOnly = computed(() => {
  // should use the rules engine

  return (
    props.isReadOnly ||
    props.modelValue.properties.isReadOnly ||
    form.rules.some(
      (rule) =>
        rule.elementGuid === props.modelValue.guid &&
        rule.ruleType === FormElementRuleType.READ_ONLY &&
        conditionsApply(rule.conditions),
    )
  );
});

const conditionsApply = (conditions: RuleConditionBase[]) => {
  if (!conditions || conditions.length === 0) return false;

  //TODO: use the rules engine

  return conditions.every((condition) => {
    switch (condition.conditionType) {
      case RuleConditionType.WORKFLOW_STATUS_BASED: {
        const workflowStatusCondition =
          condition as WorkflowStatusRuleCondition;
        const type = workflowStatusCondition.type;
        const workflowStatusInRule = workflowStatusCondition.workflowStatus;
        const currentStatus = currentWorkflowStatus.value;

        switch (type) {
          case StatusBasedConditionType.STATUS_IS:
            return currentStatus === workflowStatusInRule;
          case StatusBasedConditionType.STATUS_IS_NOT:
            return currentStatus !== workflowStatusInRule;
          case StatusBasedConditionType.STATUS_IS_AFTER:
          case StatusBasedConditionType.STATUS_IS_BEFORE:
          default:
            return false;
        }
      }

      default:
        // unexpected condition type
        console.warn(`Unexpected condition type: ${condition.conditionType}`);
        return false;
    }
  });
};

const deleteRepeatedSection = async (repeaterGroupGuid: string) => {
  if (!formResponse) {
    const index =
      testingAnswer.value.repeatableGroupGuids.indexOf(repeaterGroupGuid);
    testingAnswer.value.repeatableGroupGuids.splice(index, 1);
    return;
  }

  ElMessageBox.confirm(
    `This will permanently delete this ${elementProperties.value.itemTitle} and its answers. Continue?`,
    `Deleting ${elementProperties.value.itemTitle}`,
    {
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  ).then(async () => {
    await apiClient.formResponseAnswer
      .deleteRepeaterAnswer(
        formResponse.formDefinitionGuid,
        formResponse.guid,
        props.modelValue.guid,
        repeaterGroupGuid,
        myInfoStore.currentSysOrgGuid,
      )
      .then((res) => {
        reloadAnswers(res);
      })
      .catch(showError);
  });
};

/**
 * this method sets the repeaterGroupGuid property of an element
 * @param element
 * @param repeaterGroupGuid
 */
const setRepeaterGroupGuid = (
  element: FormElementBase,
  repeaterGroupGuid: string,
) => {
  return { ...element, repeaterGuid: repeaterGroupGuid };
};

const getScopedComponents = (repeatableGroupGuid: string) => {
  return repeatersComponents.value.map((c) =>
    setRepeaterGroupGuid(c, repeatableGroupGuid),
  );
};

onBeforeMount(async () => {
  if (formResponse) {
    if (!answer.value) {
      await createRepeatedSection();
    }
  }
});
</script>

<template id="RepeaterComponent">
  <slot name="label" :element="modelValue" />
  <!-- IF WE ARE IN A FORM RESPONSE -->
  <div v-if="formResponse" class="form-Repeater">
    <div v-if="answer?.repeatableGroupGuids?.length" class="repeated-groups">
      <div
        v-for="(repeatableGroupGuid, index) in answer?.repeatableGroupGuids"
        :key="repeatableGroupGuid"
        class="repeated-group"
      >
        <div class="header">
          <div class="label">
            {{ elementProperties.itemTitle }}
            <span v-if="elementProperties.showNumber">{{ index + 1 }}</span>
          </div>
          <el-button
            v-if="!isReadOnly"
            size="small"
            @click="deleteRepeatedSection(repeatableGroupGuid)"
          >
            {{ elementProperties.removeButtonText }}
          </el-button>
        </div>

        <div v-if="repeatersComponents.length">
          <div
            v-for="component in getScopedComponents(repeatableGroupGuid)"
            :key="component.guid"
          >
            <FormComponentRenderer
              :model-value="component"
              :show-archived="showArchived"
              :is-dragging="isDragging"
              :class="{
                highlight: editingComponent === component,
                'section-selected':
                  editingComponent?.guid === component.parentGuid,
                [component.questionCode]: true,
              }"
              :display-mode="
                isReadOnly ? FormDisplayMode.READ_ONLY : FormDisplayMode.EDIT
              "
            />
          </div>
        </div>
        <el-empty v-else description="No questions found" />
      </div>
    </div>
    <div v-else>
      <p>
        {{
          elementProperties.noItemsText ||
          "Click the button below to add an item."
        }}
      </p>
    </div>
    <el-button
      v-if="!isReadOnly"
      size="small"
      @click="createRepeatedSection(1)"
    >
      {{ elementProperties.addButtonText }}
    </el-button>
  </div>

  <!-- IF WE ARE IN THE DESIGNER -->
  <div v-else class="form-Repeater">
    <div
      v-if="testingAnswer.repeatableGroupGuids.length"
      class="repeated-groups"
    >
      <div
        v-for="(
          repeatableGroupGuid, index
        ) in testingAnswer.repeatableGroupGuids"
        :key="repeatableGroupGuid"
        class="repeated-group"
      >
        <div class="header">
          <div class="label">
            {{ elementProperties.itemTitle }}
            <span v-if="elementProperties.showNumber">{{ index + 1 }}</span>
          </div>
          <el-button
            size="small"
            :disabled="isReadOnly"
            @click="deleteRepeatedSection(repeatableGroupGuid)"
          >
            {{ elementProperties.removeButtonText }}
          </el-button>
        </div>

        <div v-if="!repeatersComponents.length" class="component-wrapper">
          <DroppablePad
            :id="modelValue.guid || modelValue.questionCode"
            :key="modelValue.guid"
            :is-dragging="isDragging && canDropInRepeater"
            @drop="
              (event: DragEvent) => handleDropComponent(event, modelValue.guid)
            "
          >
          </DroppablePad>
          <el-empty
            :image-size="100"
            description="Drag a question here to add it to this repeater"
          />
        </div>

        <div v-for="component in repeatersComponents" :key="component.guid">
          <div
            v-if="!component.isArchived || showArchived"
            :draggable="!isReadOnly"
            class="component-wrapper"
            @click="
              (event: MouseEvent) => handleClickComponent(component, event)
            "
            @dragstart="!isReadOnly && handleOnDrag($event, component)"
            @dragend="!isReadOnly && handleOnDrag($event, component)"
          >
            <DroppablePad
              :id="component.guid || component.questionCode"
              :key="modelValue.guid"
              :is-dragging="isDragging && canDropInRepeater"
              @drop="
                (event: DragEvent) =>
                  handleDropComponent(event, modelValue.guid)
              "
            >
              <FormComponentRenderer
                :model-value="
                  setRepeaterGroupGuid(component, repeatableGroupGuid)
                "
                :is-dragging="isDragging"
                :show-archived="showArchived"
                :display-mode="
                  isReadOnly ? FormDisplayMode.READ_ONLY : undefined
                "
              />
            </DroppablePad>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <DroppablePad
        :id="modelValue.guid || modelValue.questionCode"
        :key="modelValue.guid"
        :is-dragging="isDragging && canDropInRepeater"
        @drop="
          (event: DragEvent) => handleDropComponent(event, modelValue.guid)
        "
      >
        <p>
          {{
            elementProperties.noItemsText ||
            "Click the button below to add an item."
          }}
        </p>
      </DroppablePad>
    </div>
    <el-button
      size="small"
      :disabled="isReadOnly"
      @click="createRepeatedSection(1)"
    >
      {{ elementProperties.addButtonText }}
    </el-button>
  </div>
</template>

<style lang="less">
.form-Repeater {
  display: grid;
  justify-items: start;
  padding: 1em;
  gap: 1em;
  border-radius: var(--el-border-radius-base);
  background: #f1f0f0;

  .repeated-groups {
    display: grid;
    gap: 1em;
    width: 100%;
    .repeated-group {
      border-radius: var(--el-border-radius-base);
      background: white;
      padding: 0 1em 1em;
      page-break-inside: avoid;

      .header {
        display: flex;
        border-top-right-radius: var(--el-border-radius-base);
        border-top-left-radius: var(--el-border-radius-base);
        justify-content: space-between;
        background-color: var(--el-text-color-secondary);
        color: var(--background-color);
        align-items: center;
        border-bottom: 1px solid #a1a1a1;
        margin: 0 -1em 1.5em;
        padding: 0.4em 0.8em;

        h1 {
          margin: 0;
        }
      }
    }
  }
}
</style>
